// Provider chain — outermost first per plan madde 1.5:
//   AuthProvider (react-oidc-context) wraps the OIDC user lifecycle
//     against the `operators` realm (NOT customers — cross-realm
//     tokens are rejected by the backend per ADR-007 + plan madde 2.6).
//   QueryClientProvider hands TanStack Query its singleton client.
//   I18nextProvider gives every component access to the translator.
//   ErrorBoundary catches render-time crashes and renders an envelope-
//     shaped fallback (plan madde 8.3 hardens this with GlitchTip emit).
//   RouterProvider drives navigation.
//
// Order matters: TopBar uses useAuth + useTranslation; both must be in
// scope by the time the router renders. ErrorBoundary sits inside the
// providers so a crash inside the router still has access to i18n keys
// for the fallback message.

import { I18nextProvider } from "react-i18next";
import { AuthProvider } from "react-oidc-context";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router";

import { queryClient } from "@/api/queryClient";
import { oidcConfig } from "@/auth/oidc-config";
import { useOperatorAuthFetch } from "@/auth/useOperatorAuthFetch";
import { DevOperatorSwitcher } from "@/auth/DevOperatorSwitcher";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { IdleTimeoutGuard } from "@/components/IdleTimeoutGuard";
import { ToastProvider } from "@/components/ToastProvider";
import { i18n } from "@/i18n";
import { router } from "@/router";

function AuthenticatedRouter(): React.ReactElement {
  useOperatorAuthFetch();
  return <RouterProvider router={router} />;
}

export function App(): React.ReactElement {
  return (
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <ErrorBoundary>
            <IdleTimeoutGuard>
              <AuthenticatedRouter />
              {/* Toaster mounts a portal into <body>; tree position only
                  matters for i18n context inheritance (sonner reads no
                  React context, but our showErrorToast caller reads the
                  i18n t() from the caller's tree). Outside the Router so
                  toasts persist across navigation — a 401 fired right
                  before navigate() still stays visible on the next route. */}
              <ToastProvider />
              {/* DEV-ONLY; renders null (and tree-shakes) in production. */}
              <DevOperatorSwitcher />
            </IdleTimeoutGuard>
          </ErrorBoundary>
        </I18nextProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
